/**
 * Run a GQL query on an interval.  The hook returns the most recent
 * result of the query.
 *
 * The hook returns loading status only when the intial query is rendering.
 * After that, it returns success or error statuses.
 *
 * You can optionally set the maximum number of times in a row that GQL can
 * send an error response before the hook returns an error.  This is to help
 * mitigate disruption caused by transient errors.  While retries are
 * happening, the hook returns the last successful result.  If the GQL
 * query is still returning an error after the allotted number of retries,
 * the hook returns the most recent error and terminate the interval
 * schedule.  If the initial call results in an error, it is returned
 * without retries.
 *
 * The hook also returns a refetch method that can be used to trigger a
 * query outside of the normal interval schedule, e.g. in response to a user
 * action.  If the interval schedule has been stopped due a GQL error, a
 * successful refetch restarts it.
 *
 * This only triggers a re-render of the calling component if the result
 * changes. Errors results are unique and always trigger a re-render.
 *
 * There is no caching.
 */
import isEqual from "lodash.isequal";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {
    toGqlOperation,
    type ValidCacheData,
} from "@khanacademy/wonder-blocks-data";
import {SchedulePolicy, useInterval} from "@khanacademy/wonder-blocks-timing";
import {documentNode, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import {type PartialGqlContext} from "./types.ts";
import {useGql} from "./use-gql.ts";
import {useRequestIdBuilder} from "./use-request-id-builder.ts";

/**
 * A generic return type for hooks that return polling query data.
 */
export type PollingResult<TData extends ValidCacheData> =
    | {
          status: "loading";
      }
    | {
          status: "error";
          error: Error;
      }
    | {
          status: "success";
          data: TData;
      };

interface UsePollingQueryOptions<TVariables> {
    /**
     * Variables for the GQL operation.
     */
    variables: TVariables;
    /**
     * Allows overriding request context such as targetLocale and curriculum.
     */
    context?: Partial<PartialGqlContext>;
    /**
     * The interval in ms.
     */
    interval: number;
    /**
     * If true, the interval is active.
     */
    active?: boolean;
    /**
     * The number of times in a row the GQL query can return an error before the
     * hook returns an error.
     * If -1, the hook will retry indefinitely.
     * If 0, the hook will not retry.
     */
    maxRetries?: number;
    /**
     * Callback to get notified when requests start and end. This can be used to
     * track loading states.
     *
     * TODO(FEI-7334, justinmayhew): Remove this in favor of codifying the
     * inflight state as part of the result status.
     */
    onRequestNotification?: (start: boolean) => void;
}

export const usePollingQuery = <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(
    operation: GqlOperation<TData, TVariables>,
    {
        variables,
        context,
        interval,
        active = true,
        maxRetries = -1,
        onRequestNotification,
    }: UsePollingQueryOptions<TVariables>,
): [PollingResult<TData>, () => void] => {
    if (process.env.NODE_ENV !== "production") {
        const wbdOperation = toGqlOperation(documentNode(operation));
        if (wbdOperation.type !== "query") {
            throw new KAError(
                "Mutations are not valid for usePollingQuery",
                Errors.InvalidInput,
            );
        }
    }

    const [result, setInternalResult] = useState<PollingResult<TData>>({
        status: "loading",
    });
    const resultRef = useRef<PollingResult<TData>>(result);

    // All updates to `result` should go through this function instead of
    // `setInternalResult` so that the ref stays in sync.
    const setResult = (r: PollingResult<TData>): void => {
        setInternalResult(r);
        resultRef.current = r;
    };

    const retries = useRef<number>(maxRetries);
    const intervalIsActive = useRef<boolean>(active);

    const requestIdBuilder = useRequestIdBuilder();
    const requestId = requestIdBuilder(operation, variables, context || {});

    const gqlFetch = useGql();
    const query = useCallback(async () => {
        /**
         * TODO(FEI-7335): improve robustness around cancellation and having
         * multiple requests inflight concurrently.
         */
        if (onRequestNotification) {
            onRequestNotification(true);
        }
        try {
            const data = await gqlFetch(operation, {
                variables,
                context,
            });
            if (
                resultRef.current.status !== "success" ||
                !isEqual(resultRef.current.data, data)
            ) {
                setResult({status: "success", data});
            }
            if (retries.current !== -1) {
                retries.current = maxRetries;
            }
        } catch (error: any) {
            if (
                resultRef.current.status === "loading" || // Initial query call.
                retries.current === 0 // All retries have been used up.
            ) {
                setResult({status: "error", error});
                intervalIsActive.current = false;
            } else if (retries.current > 0) {
                retries.current = retries.current - 1;
            }
        } finally {
            if (onRequestNotification) {
                onRequestNotification(false);
            }
        }
        // Depending on `requestId` instead of (operation, variables, context)
        // as it already handles _deep_ equality for those three values.
        // `setResult` does not need to be included because it never changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- See above.
    }, [gqlFetch, requestId, maxRetries, onRequestNotification]);

    // Rerun the query whenever operation, variables, or context changes.
    // This includes the initial mount.
    useEffect(() => {
        query();
    }, [query]);

    // Re-run the query on an interval schedule.
    useInterval(query, interval, {
        schedulePolicy: intervalIsActive.current
            ? SchedulePolicy.Immediately
            : SchedulePolicy.OnDemand,
    });

    // Re-run the query outside of the normal interval schedule.
    // If the interval schedule was terminated due a query error
    // and the refetch is successful, restart it.
    const refetch = useCallback(() => {
        intervalIsActive.current = active;
        query();
    }, [active, query]);

    return useMemo(() => [result, refetch], [result, refetch]);
};
